import React, { useState, useEffect } from "react";
import { css, keyframes } from "@emotion/css";
import { MdEmail } from "react-icons/md";
import { IoArrowBack } from "react-icons/io5";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import OtpInput from "react-otp-input";
import { BaseLoginButton } from "../../ui/base-login-button/base-login-button";
import { GoogleButton } from "../google-button/google-button";
import { FacebookButton } from "../facebook-button/facebook-button";
import { LogoMark } from "../../../../shared/ui/logo-mark/logo-mark";
import { Input } from "../../../../shared/ui/input";
import { Label } from "../../../../shared/ui/label";
import { useRequestAuth, useVerifyAuth } from "../../hooks/auth-api.hooks";

const loginSchema = z.object({
  email: z
    .string()
    .min(1, "O E-mail é um campo obrigatório")
    .email({ message: "O E-mail digitado é inválido." }),
  otp: z.string().min(6, "O código de verificação é um campo obrigatório!"),
});

type LoginFormData = z.infer<typeof loginSchema>;

const spin = keyframes({
  "0%": { transform: "rotate(0deg)" },
  "100%": { transform: "rotate(360deg)" },
});

const card = css({
  /* Let parent control the card width via className on the wrapper.
     The card itself should stretch to 100% of that container. */
  width: "100%",
  maxWidth: "100%",
  borderRadius: "var(--spacing-md)",
  padding: "var(--spacing-xl)",
  boxShadow: "var(--shadow-300)",
  display: "flex",
  flexDirection: "column",
  gap: "var(--spacing-2xs)",
  backgroundColor: "var(--primary-color)",
  border: "1px solid rgba(var(--pcrgba-200), 0.5)",
  transition: "all 0.2s ease-in-out",
});

const logo = css({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  marginBottom: "var(--spacing-2xs)",
  "& svg": {
    width: "80px",
    height: "80px",
  },
  "@media (max-width: 480px)": {
    "& svg": {
      width: "60px",
      height: "60px",
    },
  },
});

const cardDescription = css({
  display: "flex",
  flexDirection: "column",
  textAlign: "center",
  alignItems: "center",
});

const cardHeader = css({
  position: "relative",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  marginBottom: "var(--spacing-md)",
  width: "100%",
});

const backButton = css({
  background: "none",
  border: "none",
  padding: "0.5rem",
  cursor: "pointer",
  color: "var(--pc-600)",
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "background-color 0.2s ease",
  position: "absolute",
  left: "-2rem",
  top: "50%",
  transform: "translateY(-50%)",
  fontSize: "20px",
  ":hover": {
    backgroundColor: "var(--pc-100)",
  },
});

const cardTitle = css({
  fontWeight: 700,
  fontSize: "var(--fs-600)",
  color: "var(--pc-800)",
  "@media (max-width: 920px)": {
    fontSize: "var(--fs-550)",
  },
  "@media (max-width: 480px)": {
    fontSize: "var(--fs-500)",
  },
});

const cardDescriptionText = css({
  width: "20em",
  fontSize: "var(--fs-400)",
  color: "var(--pc-700)",
  lineHeight: 1.5,
  "@media (max-width: 920px)": {
    width: "clamp(0px, 100%, 20em)",
    fontSize: "var(--fs-350)",
  },
});

const cardMessage = css({
  backgroundColor: "rgba(var(--pcrgba-100), 0.7)",
  color: "var(--pc-700)",
  borderLeft: "3px solid var(--pc-600)",
  padding: "var(--spacing-sm) var(--spacing-md)",
  margin: "var(--spacing-sm) 0",
  borderRadius: "var(--spacing-2xs)",
  fontSize: "var(--fs-350)",
  lineHeight: 1.4,
  "@media (max-width: 920px)": {
    fontSize: "var(--fs-300)",
    padding: "var(--spacing-xs) var(--spacing-sm)",
  },
});

const form = css({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: "var(--spacing-lg)",
  marginTop: "var(--spacing-md)",
});

const inputGroup = css({
  display: "flex",
  flexDirection: "column",
  gap: "var(--spacing-2xs)",
  textAlign: "left",
});

const btnGroup = css({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: "var(--spacing-md)",
});

const loginButton = css({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  backgroundColor: "var(--pc-500)",
  color: "var(--primary-color)",
  border: "none",
  transition: "all 0.2s ease-in-out",
  boxShadow: "0 4px 12px rgba(var(--pcrgba-500), 0.2)",
  minHeight: "48px",
  borderRadius: "var(--spacing-sm)",
  position: "relative",
  fontFamily: "Montserrat",
  fontSize: "var(--fs-400)",
  fontWeight: 600,
  width: "100%",
  cursor: "pointer",
  ":hover:not(:disabled)": {
    backgroundColor: "var(--pc-600)",
    boxShadow: "0 6px 15px rgba(var(--pcrgba-600), 0.3)",
    transform: "translateY(-2px)",
  },
  ":disabled": {
    opacity: 0.6,
    cursor: "not-allowed",
  },
  "@media (max-width: 920px)": {
    minHeight: "44px",
    fontSize: "var(--fs-350)",
  },
});

const btnContent = css({
  display: "flex",
  alignItems: "center",
  width: "100%",
  position: "relative",
  fontSize: "var(--fs-400)",
  fontWeight: 600,
  lineHeight: 1,
  padding: "0 0 0 24px",
  "@media (max-width: 920px)": {
    fontSize: "var(--fs-350)",
  },
});

const btnContentLoading = css({
  justifyContent: "center",
  alignItems: "center",
  padding: "0",
});

const btnIcon = css({
  position: "absolute",
  left: 0,
  fontSize: "20px",
  height: "20px",
  width: "20px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "@media (max-width: 920px)": {
    fontSize: "var(--fs-350)",
    height: "var(--fs-350)",
    width: "var(--fs-350)",
  },
});

const btnText = css({
  flex: 1,
  textAlign: "center",
});

const otpContainer = css({
  display: "flex",
  justifyContent: "space-between",
  gap: "var(--spacing-3xs)",
  width: "100%",
});

const otpInputStyle = css({
  width: "100%",
  fontSize: "var(--fs-400)",
  textTransform: "uppercase",
  padding: "var(--spacing-sm)",
  textAlign: "center",
  color: "var(--pc-800)",
  border: "1px solid rgba(var(--pcrgba-200), 0.5)",
  backgroundColor: "var(--primary-color)",
  borderRadius: "var(--spacing-xs)",
  ":focus": {
    borderColor: "var(--pc-300)",
    outline: "none",
    boxShadow: "0 0 0 2px rgba(var(--pcrgba-300), 0.2)",
  },
  "@media (max-width: 920px)": {
    fontSize: "var(--fs-350)",
    padding: "var(--spacing-sm)",
  },
  "@media (max-width: 480px)": {
    fontSize: "var(--fs-350)",
    padding: "var(--spacing-xs) 0px",
  },
});

const errorText = css({
  color: "#e53935",
  fontSize: "1.4rem",
  textAlign: "left",
  marginTop: "var(--spacing-2xs)",
});

const spinner = css({
  border: "2px solid rgba(255, 255, 255, 0.3)",
  borderTop: "2px solid white",
  borderRadius: "50%",
  width: "16px",
  height: "16px",
  animation: `${spin} 0.8s linear infinite`,
});

export interface LoginCardProps extends React.HTMLAttributes<HTMLDivElement> {
  message?: string;
  onSuccess?: (user: any) => void;
  redirectUrl?: string;
}

export const LoginCard: React.FC<LoginCardProps> = ({
  className,
  message,
  onSuccess,
  redirectUrl,
  ...rest
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const { mutate: requestAuth, isPending: isRequestingAuth } = useRequestAuth();
  const { mutate: verifyAuth, isPending: isVerifyingAuth } = useVerifyAuth();

  const {
    register,
    watch,
    setValue,
    formState: { errors },
    getValues,
    trigger,
  } = useForm<LoginFormData>({
    defaultValues: {
      email: "",
      otp: "",
    },
    resolver: zodResolver(loginSchema),
  });

  const handleEmailButtonClick = () => {
    setCurrentStep(1);
  };

  const handleRequestOtp = async () => {
    await trigger("email");
    if (errors.email) {
      return;
    }

    const email = getValues("email");
    
    requestAuth(
      { email },
      {
        onSuccess: () => {
          setError(null);
          setCurrentStep(2);
          window.history.pushState({}, "", document.URL);
        },
        onError: (err: any) => {
          const errorMessage = err?.response?.data?.message || 
            "O e-mail informado é inválido ou houve um erro. Tente novamente.";
          setError(errorMessage);
          console.error(err);
        },
      }
    );
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const email = getValues("email");
    const code = getValues("otp");

    verifyAuth(
      { email, code },
      {
        onSuccess: () => {
          const mockUser = {
            email,
            name: "User",
          };

          if (onSuccess) {
            onSuccess(mockUser);
          }
        },
        onError: (err: any) => {
          const errorMessage = err?.response?.data?.message || 
            "O código informado é inválido ou expirou.";
          setError(errorMessage);
        },
      }
    );
  };

  const handlePreviousStep = () => {
    setError(null);
    setCurrentStep((prev) => (prev <= 0 ? 0 : prev - 1));
  };

  useEffect(() => {
    const handleBackButton = (event: PopStateEvent) => {
      if (currentStep > 0) {
        handlePreviousStep();
        event.preventDefault();
      }
    };

    window.addEventListener("popstate", handleBackButton);

    return () => {
      window.removeEventListener("popstate", handleBackButton);
    };
  }, [currentStep]);

  const renderContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <>
            <p className={cardTitle}>Bem-vindo(a) à Anchor Ingressos!</p>
            <p className={cardDescriptionText}>
              Entre com a sua conta para comprar ingressos, acessar suas compras
              e outras configurações.
            </p>

            {message && <p className={cardMessage}>{message}</p>}

            <div className={btnGroup}>
              <div className={form}>
                <BaseLoginButton
                  className={loginButton}
                  onClick={handleEmailButtonClick}
                >
                  <div className={btnContent}>
                    <MdEmail className={btnIcon} />
                    <span className={btnText}>Continue com o seu e-mail</span>
                  </div>
                </BaseLoginButton>
              </div>
              
              <FacebookButton onSuccess={onSuccess} redirectUrl={redirectUrl} />

              <GoogleButton onSuccess={onSuccess} redirectUrl={redirectUrl} />
            </div>
          </>
        );

      case 1:
        return (
          <>
            <div className={cardHeader}>
              <button className={backButton} onClick={handlePreviousStep}>
                <IoArrowBack />
              </button>
              <p className={cardTitle}>Entre com seu e-mail</p>
            </div>
            <p className={cardDescriptionText}>
              Digite seu e-mail para receber um código de verificação
            </p>

            <div className={form}>
              <div className={inputGroup}>
                <Label htmlFor="email" error={!!errors.email}>
                  Email
                </Label>
                <Input
                  {...register("email")}
                  id="email"
                  placeholder="Digite seu e-mail..."
                  error={!!errors.email}
                />
                {errors.email && (
                  <p className={errorText}>{errors.email.message}</p>
                )}
                {currentStep === 1 && error && (
                  <p className={errorText}>{error}</p>
                )}
              </div>
              <BaseLoginButton
                className={loginButton}
                onClick={handleRequestOtp}
                disabled={isRequestingAuth}
              >
                <div
                  className={`${btnContent} ${
                    isRequestingAuth ? btnContentLoading : ""
                  }`}
                >
                  {isRequestingAuth ? (
                    <div className={spinner}></div>
                  ) : (
                    <>
                      <MdEmail className={btnIcon} />
                      <span className={btnText}>Solicitar código</span>
                    </>
                  )}
                </div>
              </BaseLoginButton>
            </div>
          </>
        );

      case 2:
        return (
          <>
            <div className={cardHeader}>
              <button className={backButton} onClick={handlePreviousStep}>
                <IoArrowBack />
              </button>
              <p className={cardTitle}>Verificação</p>
            </div>
            <p className={cardDescriptionText}>
              Você receberá um código por e-mail. Cheque sua caixa de entrada e
              spam/lixo eletrônico.
            </p>

            <form onSubmit={handleVerifyOtp} className={form}>
              <OtpInput
                value={watch("otp")}
                onChange={(otpValue) => setValue("otp", otpValue)}
                numInputs={6}
                containerStyle={otpContainer}
                inputStyle={otpInputStyle}
                inputType="tel"
                shouldAutoFocus
                renderInput={(props, i) => <input {...props} style={{width: "100%", textAlign: "center"}} key={i} />}
              />
              {errors.otp && <p className={errorText}>{errors.otp.message}</p>}
              {currentStep === 2 && error && (
                <p className={errorText}>{error}</p>
              )}
              <BaseLoginButton
                type="submit"
                className={loginButton}
                disabled={isVerifyingAuth}
              >
                <div
                  className={`${btnContent} ${
                    isVerifyingAuth ? btnContentLoading : ""
                  }`}
                >
                  {isVerifyingAuth ? (
                    <div className={spinner}></div>
                  ) : (
                    <span className={btnText}>Verificar código</span>
                  )}
                </div>
              </BaseLoginButton>
            </form>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div className={className} {...rest}>
      <div className={card}>
        <div className={logo}>
          <LogoMark />
        </div>
        <div className={cardDescription}>{renderContent()}</div>
      </div>
    </div>
  );
};
